import React, { useCallback, useEffect } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Breadcrumbs, BreadcrumbsItem, H1, PageActions, PageHeader, PageHeaderAction, PageHeaderAvatar, PageHeaderAvatarLayout, PageHeaderContent, PaymentOverviewStats, PaymentStatusPills, Section, SectionHeader, Text } from 'components';
import { lineHeight, theme, toRem } from 'design';
import { usePayPeriods, useQueryString } from 'hooks';
import { URL } from 'routes/routeConstants';
import { BusinessDayLabel } from 'screens/Payments/styles';
import { UserNames } from 'screens/UserDetail/components';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { getSiteEmployeePayoutsRequest, Payouts, selectPayouts, selectTotalCountPayouts } from 'store/Collections/Payouts';
import { selectEmployee } from 'store/Employee';
import { selectCurrentEmployeeOrg, selectCurrentEmployeeSite } from 'store/Employers';
import { getSitePayoutStatsRequest, selectPayoutStats, Stats } from 'store/Stats';
import { IStore } from 'store/types';
import { ApiQueryOperator, IPayPeriod } from 'types';
import { payPeriodDateFilters } from 'utils/payPeriods';
import { DEFAULT_OFFSET, DEFAULT_PAGE_SIZE } from 'utils/table';
import { PayPeriodSelector, PayrollPeriodWrapper, PayrollTipsUserDetailPayouts } from './components';

const SiteName = styled(Text)`
  margin-top: ${toRem(theme.space.xs)};
  color: ${theme.color.neutral[600]};
  font-size: ${toRem(theme.font.size.lg)};
  line-height: ${lineHeight(theme.font.size.lg)};
`;

interface IParams {
  id: string;
  startDate: string;
  endDate: string;
}

const ReportsEmployeePayrollTips: React.FC = () => {
  const { id, startDate, endDate } = useParams<IParams>();
  const dispatch = useDispatch();
  const history = useHistory();
  const currentOrganizationId = useSelector(selectCurrentEmployeeOrg)?.id;
  const currentSite = useSelector(selectCurrentEmployeeSite);
  const currentSiteId = currentSite?.id;
  const employeeDetail = useSelector(selectEmployee());
  const payouts = useSelector(selectPayouts());
  const payoutsTotal = useSelector(selectTotalCountPayouts());
  const payoutStats = useSelector(selectPayoutStats());
  const isPayoutsLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Payouts.GET_SITE_EMPLOYEE_PAYOUTS_REQUEST));
  const isLoadingPayoutStats = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Stats.GET_SITE_PAYOUT_STATS_REQUEST));

  // The route names the period; the URL query holds the table's page, page size and sort.
  const [urlQuery, setUrlQuery] = useQueryString('reportsEmployeePayrollTips', window.location, { limit: DEFAULT_PAGE_SIZE, offset: DEFAULT_OFFSET, sort: '-payment_date' });

  const { payPeriods, isLoadingFirstPage: isPayPeriodsLoading, isLoadingMore, loadMore } = usePayPeriods(currentOrganizationId, currentSiteId, startDate);
  const newestPayPeriod = payPeriods[0];
  const selectedPayPeriod = payPeriods.find((period) => period.start === startDate && period.end === endDate);
  const isRoutePeriodLoaded = !!selectedPayPeriod;

  const onPeriodChange = (period: IPayPeriod) => {
    setUrlQuery({ offset: DEFAULT_OFFSET });
    history.replace({ pathname: `${URL.ReportsEmployeePayPeriod}/${id}/${period.start}/${period.end}`, search: window.location.search });
  };

  // The route's period is always loaded unless it belongs to another site; then fall back to the newest.
  useEffect(() => {
    if (newestPayPeriod && !isRoutePeriodLoaded) onPeriodChange(newestPayPeriod);
  }, [newestPayPeriod?.start, isRoutePeriodLoaded]);

  // Stats for the period. Also reloaded after a payout changes; the list is not, since the store patches the row.
  const fetchStats = useCallback(() => {
    if (currentSiteId && isRoutePeriodLoaded) dispatch(getSitePayoutStatsRequest(currentSiteId, startDate, endDate, { q: [{ key: 'employee_id', operator: ApiQueryOperator.eq, value: id }] }));
  }, [dispatch, id, currentSiteId, startDate, endDate, isRoutePeriodLoaded]);
  useEffect(fetchStats, [fetchStats]);

  // The period's payouts, one page at a time.
  useEffect(() => {
    if (!currentSiteId || !isRoutePeriodLoaded) return;
    dispatch(getSiteEmployeePayoutsRequest(currentSiteId, id, { ...urlQuery, q: payPeriodDateFilters({ start: startDate, end: endDate }) }));
  }, [dispatch, id, currentSiteId, startDate, endDate, isRoutePeriodLoaded, urlQuery]);

  const fullName = employeeDetail?.name || '';
  const employeeLoading = isPayoutsLoading || employeeDetail?.id !== id;
  const isLoading = isPayPeriodsLoading || isPayoutsLoading;

  return (
    <>
      <PageHeader>
        <PageHeaderContent stackable>
          <>
            <PageHeaderAvatarLayout>
              <PageHeaderAvatar
                username={fullName}
                size="large"
                id={employeeDetail?.id}
                image={employeeDetail?.user?.avatarUrl}
                hasPaymentMethod={!!employeeDetail?.user?.card}
                isLoading={employeeLoading}
                isDismissed={!!employeeDetail?.deletedAt}
              />
              <UserNames>
                <Breadcrumbs>
                  <BreadcrumbsItem data-cy="payments-breadcrumblink" label="Reports" to={URL.ReportsPayPeriod} />
                  <BreadcrumbsItem data-cy="make-payments-breadcrumblink" label="Employee Report" />
                </Breadcrumbs>
                {employeeLoading ? <Skeleton width={154} /> : <H1 marginBottom={0}>{fullName}</H1>}
                {employeeLoading ? <Skeleton width={100} /> : <SiteName>{currentSite?.name}</SiteName>}
              </UserNames>
            </PageHeaderAvatarLayout>

            <PageActions>
              {!employeeLoading && !employeeDetail?.deletedAt && employeeDetail?.sites && employeeDetail?.sites.find((s) => s.id === currentSiteId) && (
                <PageHeaderAction isLoading={employeeLoading} label="View User Details" appearance="tertiary" to={`/users/${employeeDetail?.id}`} />
              )}
            </PageActions>
          </>
        </PageHeaderContent>
      </PageHeader>

      <Section data-cy="table">
        <SectionHeader title="Payments" value={`(${payoutsTotal || 0})`} isLoading={isLoading}>
          <BusinessDayLabel>{isLoading ? <Skeleton width={90} /> : 'Payroll Period'}</BusinessDayLabel>
          <PayrollPeriodWrapper>
            <PayPeriodSelector payPeriods={payPeriods} selected={selectedPayPeriod} isLoading={isPayPeriodsLoading} isLoadingMore={isLoadingMore} onLoadMore={loadMore} onChange={onPeriodChange} />
          </PayrollPeriodWrapper>
        </SectionHeader>

        <PaymentStatusPills payoutStats={payoutStats} isLoading={isLoadingPayoutStats} />
        <PaymentOverviewStats payoutStats={payoutStats} isLoading={isLoadingPayoutStats} hideHouseAccount />

        <PayrollTipsUserDetailPayouts
          payouts={payouts}
          recordsPerPage={urlQuery.limit}
          setRecordsPerPage={(limit: number) => setUrlQuery({ limit })}
          offset={urlQuery.offset}
          setOffset={(offset: number) => setUrlQuery({ offset })}
          sort={urlQuery.sort}
          setSort={(sort: string) => setUrlQuery({ sort, offset: DEFAULT_OFFSET })}
          totalRecords={payoutsTotal}
          isLoading={isLoading}
          onUpdate={fetchStats}
          appearance="default"
        />
      </Section>
    </>
  );
};

export default ReportsEmployeePayrollTips;
